/**
 * Servicio de Validación de Pacientes
 * Gestiona la búsqueda y validación de pacientes existentes en Supabase
 */

import { supabase } from '../lib/supabase/client';
import type { ExistingPatient } from '../components/Booking/BookingContext';

// SEC-059: mensaje genérico cuando search_patient_by_rut / create_patient_simple
// responden 429 (rate-limit server-side). Mismo texto en ambas RPCs — se
// comparte acá para no duplicarlo entre servicios.
export const RATE_LIMIT_MESSAGE =
  'Demasiados intentos. Esperá unos minutos o llamanos al +56 9 7800 9119.';

// SEC-062: create_patient_simple devuelve NULL cuando el RUT es nuevo pero el
// email ya pertenece a otro paciente (no se devuelve el id ajeno). Mensaje
// propio, sin confirmar ni negar que el email exista.
export const PATIENT_CONFLICT_MESSAGE =
  'No pudimos completar tu registro con los datos ingresados. Si ya fuiste paciente de Conectemos, llamanos al +56 9 7800 9119 y te ayudamos.';

export interface PatientValidationResult {
  isExisting: boolean;
  patient?: ExistingPatient;
  preferredTherapist?: {
    id: string;
    name: string;
  } | null;
  lastConsultation?: {
    date: string;
    type: string;
  } | null;
  error?: string;
  dbError?: boolean;
  // SEC-059: true cuando la RPC respondió PT429 (rate-limit). Distinto de
  // dbError — no es una falla de BD, es un límite de tasa; el mensaje ya
  // viene listo para mostrar tal cual (no es error crudo de Supabase).
  rateLimited?: boolean;
}

export class PatientValidationService {
  /**
   * Validar dígito verificador de RUT chileno — algoritmo Módulo 11
   * Retorna true si el dígito verificador es correcto.
   */
  static validateRUTCheckDigit(rut: string): boolean {
    // Eliminar puntos, guión y convertir K a mayúscula
    const clean = rut.replace(/[^0-9kK]/g, '').toUpperCase();
    if (clean.length < 2) return false;

    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);

    // El cuerpo debe ser numérico
    if (!/^\d+$/.test(body)) return false;

    // Algoritmo Módulo 11
    const digits = body.split('').reverse();
    const serie = [2, 3, 4, 5, 6, 7];
    let sum = 0;

    digits.forEach((digit, index) => {
      sum += parseInt(digit, 10) * serie[index % serie.length];
    });

    const remainder = 11 - (sum % 11);
    let expectedDV: string;

    if (remainder === 11) {
      expectedDV = '0';
    } else if (remainder === 10) {
      expectedDV = 'K';
    } else {
      expectedDV = String(remainder);
    }

    return dv === expectedDV;
  }

  /**
   * Formatear RUT para búsquedas
   */
  static formatRUTForSearch(rut: string): string {
    // Limpiar RUT y aplicar formato estándar
    const cleanRut = rut.replace(/[^0-9kK]/g, '');
    
    if (cleanRut.length <= 1) return cleanRut;
    if (cleanRut.length <= 4) return cleanRut.replace(/(\d{1,3})/, '$1');
    if (cleanRut.length <= 7) return cleanRut.replace(/(\d{1,2})(\d{1,3})/, '$1.$2');
    return cleanRut.replace(/(\d{1,2})(\d{3})(\d{3})(\w{1})/, '$1.$2.$3-$4');
  }

  /**
   * Validar formato básico de RUT (longitud y caracteres)
   */
  static validateRUTFormat(rut: string): boolean {
    const cleanRut = rut.replace(/[^0-9kK]/g, '');
    return cleanRut.length >= 8 && cleanRut.length <= 9;
  }

  /**
   * Documento extranjero tal como se guarda: sin espacios ni guiones, en
   * mayúsculas. FUNC-122: la BD cuenta el largo sobre este valor, no sobre
   * lo tipeado.
   */
  static normalizeForeignDocument(doc: string): string {
    return doc.trim().replace(/[\s\-]/g, '').toUpperCase();
  }

  /**
   * Validar documento de identidad extranjero (DNI, pasaporte, cédula).
   * Acepta 6–12 caracteres alfanuméricos (sin espacios ni guiones).
   * FUNC-122: mínimo 6 alineado con el CHECK valid_rut de pacientes
   * (6–12) — antes el frontend aceptaba 4 y la BD rechazaba < 8.
   * Retorna null si es válido, o un mensaje de error.
   */
  static validateForeignDocument(doc: string): string | null {
    const clean = this.normalizeForeignDocument(doc);
    if (clean.length < 6 || clean.length > 12) {
      return 'El documento debe tener entre 6 y 12 caracteres';
    }
    if (!/^[a-zA-Z0-9]+$/.test(clean)) {
      return 'Solo se permiten letras y números';
    }
    return null;
  }

  /**
   * Validar RUT completo: formato + dígito verificador.
   * Retorna null si es válido, o un mensaje de error descriptivo.
   */
  static validateRUT(rut: string): string | null {
    if (!this.validateRUTFormat(rut)) {
      return 'RUT inválido — formato incorrecto (debe tener 8 o 9 caracteres sin puntos)';
    }
    if (!this.validateRUTCheckDigit(rut)) {
      return 'RUT inválido — dígito verificador incorrecto';
    }
    return null;
  }

  /**
   * ¿El RUT corresponde a un paciente bloqueado administrativamente por la
   * clínica? Usa la RPC `rut_esta_bloqueado` (SECURITY DEFINER, anon) — devuelve
   * SÓLO el booleano, sin ningún dato del paciente. Ante cualquier error de red
   * o de BD retorna `false`: el bloqueo real igual lo hace cumplir el trigger
   * `trg_consultas_rechaza_paciente_bloqueado` server-side, este chequeo es sólo
   * para mostrar el aviso antes de que el paciente avance en el wizard.
   */
  static async isRutBlocked(rut: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('rut_esta_bloqueado', { p_rut: rut });
      if (error) return false;
      return data === true;
    } catch {
      return false;
    }
  }

  /**
   * Buscar paciente por RUT en Supabase.
   * Usa la RPC `search_patient_by_rut` (SECURITY DEFINER) para que el rol anon
   * pueda verificar un RUT específico sin acceso directo a la tabla pacientes.
   */
  static async searchPatientByRUT(rut: string): Promise<PatientValidationResult> {
    try {
      // Validar formato y dígito verificador antes de consultar la BD
      const validationError = this.validateRUT(rut);
      if (validationError) {
        return { isExisting: false, error: validationError };
      }

      // Buscar usando RPC con SECURITY DEFINER — no requiere permisos RLS directos
      const { data: patients, error: searchError } = await supabase
        .rpc('search_patient_by_rut', { p_rut: rut });

      if (searchError) {
        // SEC-059: rate-limit server-side (PT429) — no es un error de BD,
        // no tiene sentido "reintentar ya" como con dbError.
        if (searchError.code === 'PT429' || searchError.hint === 'rate_limited') {
          return {
            isExisting: false,
            rateLimited: true,
            error: RATE_LIMIT_MESSAGE
          };
        }

        console.error('❌ Error en búsqueda de paciente');
        return {
          isExisting: false,
          dbError: true,
          error: 'No pudimos verificar tu RUT en este momento. Por favor, intenta nuevamente.'
        };
      }

      if (!patients || patients.length === 0) {
        return {
          isExisting: false
        };
      }

      const patient = patients[0];

      // Buscar el terapeuta más frecuente del paciente
      // SEC-057: get_preferred_therapist ahora exige p_patient_rut (2do factor de
      // ownership server-side) — se pasa el mismo `rut` que el caller ya validó arriba.
      const preferredTherapist = await this.findPreferredTherapist(patient.id, rut);

      // Buscar última consulta
      // SEC-057: mismo gate en get_last_consultation.
      const lastConsultation = await this.getLastConsultation(patient.id, rut);

      // SEC-057: `patient` (fila de search_patient_by_rut) ya NO trae email/telefono
      // — se sacaron del RETURN de la RPC (filtraban contacto completo por RUT
      // adivinado, sin login). ExistingPatient tampoco los tiene más; se recolectan
      // en un paso propio dentro de ExistingPatientBooking.tsx.
      const existingPatient: ExistingPatient = {
        id: patient.id,
        nombre: patient.nombre,
        apellido: patient.apellido,
        rut: patient.rut,
        therapistId: preferredTherapist?.id,
        tarifa_diferenciada: patient.tarifa_diferenciada ?? undefined,
      };

      return {
        isExisting: true,
        patient: existingPatient,
        preferredTherapist,
        lastConsultation
      };

    } catch (error) {
      console.error('💥 Error crítico en búsqueda de paciente');
      return {
        isExisting: false,
        dbError: true,
        error: 'No pudimos verificar tu RUT en este momento. Por favor, intenta nuevamente.'
      };
    }
  }

  /**
   * Encontrar el terapeuta preferido del paciente (más consultas).
   * Usa la RPC `get_preferred_therapist` (SECURITY DEFINER) — rol anon no
   * tiene acceso directo a `consultas`.
   * SEC-057: exige `p_patient_rut` — gate de ownership server-side (paciente_id +
   * rut deben pertenecer al mismo registro). Ante RUT vacío/incorrecto la RPC
   * tira excepción, capturada abajo como cualquier otro error — nunca rompe la UI.
   */
  private static async findPreferredTherapist(patientId: string, rut: string) {
    try {
      const { data, error } = await supabase
        .rpc('get_preferred_therapist', { p_paciente_id: patientId, p_patient_rut: rut });

      if (error || !data || data.length === 0) {
        return null;
      }

      const preferred = data[0];
      return {
        id: preferred.therapist_slug || preferred.therapist_id,
        name: preferred.therapist_name
      };
    } catch (error) {
      console.error('❌ Error buscando terapeuta preferido:', error);
      return null;
    }
  }

  /**
   * Obtener última consulta del paciente.
   * Usa la RPC `get_last_consultation` (SECURITY DEFINER) — rol anon no
   * tiene acceso directo a `consultas`.
   * SEC-057: exige `p_patient_rut` — mismo gate de ownership que get_preferred_therapist.
   */
  private static async getLastConsultation(patientId: string, rut: string) {
    try {
      const { data, error } = await supabase
        .rpc('get_last_consultation', { p_paciente_id: patientId, p_patient_rut: rut });

      if (error || !data || data.length === 0) {
        return null;
      }

      const consultation = data[0];

      return {
        date: consultation.fecha_hora,
        type: consultation.tipo_consulta
      };
    } catch (error) {
      console.error('❌ Error obteniendo última consulta:', error);
      return null;
    }
  }

  /**
   * Método de debugging para simular pacientes de prueba.
   * SEC-057: ExistingPatient ya no lleva email/telefono (ver definición en
   * BookingContext.tsx) — se sacaron de estos fixtures por lo mismo.
   */
  static getTestPatients(): { [key: string]: ExistingPatient } {
    return {
      '12345678-9': {
        id: 'test_patient_1',
        nombre: 'María José',
        apellido: 'González Silva',
        rut: '12.345.678-9',
        therapistId: 'angel-diaz'
      },
      '87654321-0': {
        id: 'test_patient_2',
        nombre: 'Carlos Alberto',
        apellido: 'Rodríguez Méndez',
        rut: '8.765.432-1',
        therapistId: 'carola-campos'
      },
      '20675794-9': {
        id: 'test_patient_real',
        nombre: 'Paciente',
        apellido: 'Real',
        rut: '20.675.794-9',
        therapistId: 'angel-diaz'
      }
    };
  }
}

export default PatientValidationService;